import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, setAccessToken, clearAccessToken } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

// JWT expires in 15 minutes. We refresh 60s before expiry.
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000;
const REFRESH_BEFORE_MS        = 60 * 1000;

// ── Persist user to localStorage so page refresh doesn't log out ─────────────
const STORAGE_KEY = 'wb_user';
function saveUser(u)  { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(u)); } catch {} }
function loadUser()   { try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; } }
function clearUser()  { try { localStorage.removeItem(STORAGE_KEY); } catch {} }

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(loadUser); // hydrate from localStorage immediately
  const [loading, setLoading] = useState(true); // true until session is confirmed/denied
  const refreshTimer          = useRef(null);

  // ── Schedule proactive token refresh ──────────────────────────────────────
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = ACCESS_TOKEN_LIFETIME_MS - REFRESH_BEFORE_MS; // 14 min
    refreshTimer.current = setTimeout(async () => {
      try {
        const { data } = await authAPI.refresh();
        setAccessToken(data.accessToken);
        scheduleRefresh(); // reschedule
      } catch {
        // Cookie expired or revoked — clear state
        clearAccessToken();
        setUser(null);
      }
    }, delay);
  }, []);

  const stopRefresh = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  // ── On mount: try to restore session from HttpOnly cookie ─────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Will succeed if a valid refresh token cookie exists
        const { data: refreshData } = await authAPI.refresh();
        if (cancelled) return;
        setAccessToken(refreshData.accessToken);

        // Fetch current user profile
        const { data: meData } = await authAPI.getMe();
        if (cancelled) return;
        saveUser(meData.user);
        setUser(meData.user);
        scheduleRefresh();
      } catch {
        if (!cancelled) {
          // SAFARI FIX: Safari may block the HttpOnly cookie on first load after login
          // (strict ITP / cross-site tracking prevention). If we have a cached user
          // in localStorage, keep them logged in visually and let the next API call's
          // 401 handler trigger a proper re-login. Only clear state if no cached user.
          const cached = loadUser();
          if (cached) {
            // Keep the cached user — don't flash a white screen
            // The access token will be refreshed on the first 401 via interceptor
            setUser(cached);
          } else {
            clearAccessToken();
            clearUser();
            setUser(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      stopRefresh();
    };
  }, []); // eslint-disable-line

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    setAccessToken(data.accessToken);
    saveUser(data.user);
    setUser(data.user);
    scheduleRefresh();
    return data.user;
  }, [scheduleRefresh]);

  // ── Register — returns server message, no auto-login ─────────────────────
  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    return data;
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async (showToast = true) => {
    stopRefresh();
    try { await authAPI.logout(); } catch { /* ignore — still clear client state */ }
    clearAccessToken();
    clearUser();
    setUser(null);
    if (showToast) toast.success('Logged out successfully.');
  }, [stopRefresh]);

  // ── Sync user state after profile updates ─────────────────────────────────
  const updateUser = useCallback((updated) => {
    saveUser(updated);
    setUser(updated);
  }, []);

  // ── Re-fetch user from server (e.g. after identity approval) ─────────────
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authAPI.getMe();
      saveUser(data.user);
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  }, []);

  const isAdmin        = user?.role === 'admin';
  const isVerified     = user?.isEmailVerified;

  return (
    <AuthContext.Provider value={{
      user,
      loading,     // true while checking session on mount
      isAdmin,
      isVerified,
      login,
      register,
      logout,
      updateUser,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};